/**
 * Comprehensive Vehicle Info Accordion - displays all locksmith data
 * 17 detailed sections matching the original Base44 app
 */
import React, { useState } from 'react';
import { ChevronDown, Key, Cpu, Wrench, Zap, FileSearch, Lightbulb, DollarSign, AlertTriangle, Lock, Radio, Sparkles, Shield, FileText, Bug, Calculator, Settings } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';

const SECTIONS = [
  { id: 'key-blank', title: 'Key Blank Details', icon: Key, color: 'cyan', alwaysOpen: true },
  { id: 'key-info', title: 'Key Information Summary', icon: Key, color: 'blue', alwaysOpen: true },
  { id: 'programming', title: 'Programming', icon: Cpu, color: 'purple', alwaysOpen: true },
  { id: 'equipment', title: 'Compatible Equipment', icon: Wrench, color: 'orange' },
  { id: 'transponder', title: 'Transponder & Chip', icon: Radio, color: 'fuchsia' },
  { id: 'smart-key', title: 'Smart Key System', icon: Sparkles, color: 'lime' },
  { id: 'ignition', title: 'Ignition Info & Pricing', icon: Zap, color: 'amber' },
  { id: 'door-locks', title: 'Door Locks & Pricing', icon: Lock, color: 'indigo' },
  { id: 'diagnostics', title: 'Diagnostic Procedures', icon: FileSearch, color: 'red' },
  { id: 'pricing', title: 'Pricing & Profit', icon: DollarSign, color: 'green' },
  { id: 'cost-breakdown', title: 'Cost Breakdown', icon: Calculator, color: 'teal' },
  { id: 'warnings', title: 'Warnings & Issues', icon: AlertTriangle, color: 'red' },
  { id: 'field-tips', title: 'Field Tips & Best Practices', icon: Lightbulb, color: 'yellow' },
  { id: 'troubleshooting', title: 'Troubleshooting', icon: Bug, color: 'violet' },
  { id: 'service-bulletins', title: 'Service Bulletins', icon: FileText, color: 'rose' },
  { id: 'ecu-bcm', title: 'ECU / BCM Programming', icon: Settings, color: 'purple' },
  { id: 'supplier', title: 'Supplier & Sourcing', icon: FileText, color: 'sky' },
];

const COLOR_MAP = {
  blue: 'bg-blue-500/10 border-blue-500/30 hover:border-blue-500/60',
  cyan: 'bg-cyan-500/10 border-cyan-500/30 hover:border-cyan-500/60',
  purple: 'bg-purple-500/10 border-purple-500/30 hover:border-purple-500/60',
  orange: 'bg-orange-500/10 border-orange-500/30 hover:border-orange-500/60',
  amber: 'bg-amber-500/10 border-amber-500/30 hover:border-amber-500/60',
  red: 'bg-red-500/10 border-red-500/30 hover:border-red-500/60',
  yellow: 'bg-yellow-500/10 border-yellow-500/30 hover:border-yellow-500/60',
  green: 'bg-green-500/10 border-green-500/30 hover:border-green-500/60',
  indigo: 'bg-indigo-500/10 border-indigo-500/30 hover:border-indigo-500/60',
  fuchsia: 'bg-fuchsia-500/10 border-fuchsia-500/30 hover:border-fuchsia-500/60',
  lime: 'bg-lime-500/10 border-lime-500/30 hover:border-lime-500/60',
  rose: 'bg-rose-500/10 border-rose-500/30 hover:border-rose-500/60',
  violet: 'bg-violet-500/10 border-violet-500/30 hover:border-violet-500/60',
  teal: 'bg-teal-500/10 border-teal-500/30 hover:border-teal-500/60',
  sky: 'bg-sky-500/10 border-sky-500/30 hover:border-sky-500/60',
};

const ICON_COLOR_MAP = {
  blue: 'text-blue-500',
  cyan: 'text-cyan-500',
  purple: 'text-purple-500',
  orange: 'text-orange-500',
  amber: 'text-amber-500',
  red: 'text-red-500',
  yellow: 'text-yellow-500',
  green: 'text-green-500',
  indigo: 'text-indigo-500',
  fuchsia: 'text-fuchsia-500',
  lime: 'text-lime-500',
  rose: 'text-rose-500',
  violet: 'text-violet-500',
  teal: 'text-teal-500',
  sky: 'text-sky-500',
};

const InfoRow = ({ label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex justify-between items-start gap-4 py-2 border-b border-border/50 last:border-b-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right max-w-[60%]">
        {typeof value === 'boolean' ? (value ? '✓ Yes' : '✗ No') : value}
      </span>
    </div>
  );
};

const ListBlock = ({ items, emptyText = 'No data' }) => {
  if (!items || !Array.isArray(items) || items.length === 0) {
    return <p className="text-sm text-muted-foreground italic">{emptyText}</p>;
  }
  return (
    <ul className="space-y-1">
      {items.map((item, idx) => (
        <li key={idx} className="text-sm flex items-start gap-2">
          <span className="text-primary mt-1">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
};

const ProcedureBlock = ({ text, emptyText = 'No procedure documented' }) => {
  if (!text) return <p className="text-sm text-muted-foreground italic">{emptyText}</p>;
  return <p className="text-sm leading-relaxed whitespace-pre-line">{text}</p>;
};

const RetailerPricingTable = ({ pricing, title = "Retailer Pricing" }) => {
  if (!pricing || !Array.isArray(pricing) || pricing.length === 0) return null;
  
  // Find best price among available
  const availablePrices = pricing.filter(p => p.available !== false && p.price).map(p => p.price);
  const bestPrice = availablePrices.length > 0 ? Math.min(...availablePrices) : null;

  // Add UTM/affiliate tracking params to outbound retailer links.
  // Some retailers (Amazon) use ?tag=; most use utm_source. We attach both
  // so analytics can attribute referrals back to My Key Boss.
  const withAffiliate = (url, retailer) => {
    if (!url) return url;
    try {
      const u = new URL(url);
      const host = u.hostname.toLowerCase();
      const affId = process.env.REACT_APP_AFFILIATE_ID || 'mykeyboss-20';
      if (host.includes('amazon.')) {
        if (!u.searchParams.get('tag')) u.searchParams.set('tag', affId);
      }
      if (!u.searchParams.get('utm_source')) u.searchParams.set('utm_source', 'mykeyboss');
      if (!u.searchParams.get('utm_medium')) u.searchParams.set('utm_medium', 'vehicle_lookup');
      if (!u.searchParams.get('utm_campaign')) u.searchParams.set('utm_campaign', 'retailer_pricing');
      return u.toString();
    } catch (_) {
      // If URL is malformed, fall back to the original.
      return url;
    }
  };

  const trackClick = (item) => {
    // Lightweight analytics hook — sent only if window.gtag is available.
    try {
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'retailer_click', {
          retailer: item.retailer,
          price: item.price,
          section: title,
        });
      }
    } catch (_) {
      /* noop */
    }
  };

  return (
    <div className="space-y-2 pt-2" data-testid="retailer-pricing">
      <p className="text-sm font-semibold mb-1 flex items-center gap-2">
        <DollarSignIcon className="w-4 h-4 text-green-500" />
        {title}
      </p>
      <div className="overflow-x-auto rounded-lg border border-border/50">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-2 font-medium">Retailer</th>
              <th className="text-right p-2 font-medium">Price</th>
              <th className="text-center p-2 font-medium">Status</th>
              <th className="text-left p-2 font-medium hidden md:table-cell">Notes</th>
              <th className="text-center p-2 font-medium">Link</th>
            </tr>
          </thead>
          <tbody>
            {pricing.map((item, idx) => {
              const isBest = item.price === bestPrice && item.available !== false;
              const trackedUrl = withAffiliate(item.url, item.retailer);
              return (
                <tr 
                  key={idx} 
                  className={`border-t border-border/30 hover:bg-muted/30 transition-colors ${isBest ? 'bg-green-500/5' : ''}`}
                  data-testid={`retailer-row-${(item.retailer || '').toLowerCase().replace(/\s+/g,'-')}`}
                >
                  <td className="p-2 font-medium">
                    {item.retailer}
                    {isBest && <Badge className="ml-2 bg-green-500/20 text-green-500 border-0 text-[10px]">BEST</Badge>}
                  </td>
                  <td className="p-2 text-right font-mono font-semibold">
                    {item.price ? `$${item.price.toFixed(2)}` : 'N/A'}
                  </td>
                  <td className="p-2 text-center">
                    {item.available === false ? (
                      <Badge variant="outline" className="text-[10px] bg-red-500/10 text-red-500 border-red-500/30">Out</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] bg-green-500/10 text-green-500 border-green-500/30">In Stock</Badge>
                    )}
                  </td>
                  <td className="p-2 text-xs text-muted-foreground hidden md:table-cell">{item.notes || '—'}</td>
                  <td className="p-2 text-center">
                    {trackedUrl && (
                      <a 
                        href={trackedUrl} 
                        target="_blank" 
                        rel="noopener noreferrer sponsored" 
                        onClick={() => trackClick(item)}
                        className="text-primary hover:underline text-xs inline-flex items-center gap-1"
                        data-testid={`retailer-link-${(item.retailer || '').toLowerCase().replace(/\s+/g,'-')}`}
                      >
                        Visit ↗
                      </a>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {bestPrice && (
        <p className="text-xs text-muted-foreground">
          💰 Best price: <span className="text-green-500 font-semibold">${bestPrice.toFixed(2)}</span>
        </p>
      )}
    </div>
  );
};

// Reuse DollarSign icon
const DollarSignIcon = DollarSign;

const formatCurrency = (v) => v ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v) : 'N/A';

export const renderSection = (id, vehicle) => {
  switch (id) {
    case 'key-info':
      return (
        <div className="space-y-2" data-testid="section-key-info">
          <InfoRow label="Key Type" value={vehicle.key_type} />
          <InfoRow label="Part Number" value={vehicle.key_blank_part_number} />
          <InfoRow label="FCC ID" value={vehicle.fcc_id} />
          <InfoRow label="Chip Type" value={vehicle.chip_type} />
          <InfoRow label="Frequency" value={vehicle.frequency || vehicle.key_blank_frequency} />
          <InfoRow label="Programming Method" value={vehicle.programming_method} />
          <InfoRow label="Recommended Programmer" value={vehicle.recommended_programmer} />
          <InfoRow label="Estimated Time" value={vehicle.estimated_time_minutes ? `${vehicle.estimated_time_minutes} min` : null} />
        </div>
      );

    case 'key-blank':
      return (
        <div className="space-y-2">
          <InfoRow label="OEM Part" value={vehicle.key_blank_oem_part || vehicle.key_blank_part_number} />
          <InfoRow label="Part Number" value={vehicle.key_blank_part_number} />
          <InfoRow label="ILCO Code" value={vehicle.key_blank_ilco} />
          <InfoRow label="Bianchi Code" value={vehicle.key_blank_bianchi} />
          <InfoRow label="Jet Code" value={vehicle.key_blank_jet} />
          <InfoRow label="Blade Profile" value={vehicle.key_blank_blade} />
          <InfoRow label="Material" value={vehicle.key_blank_material} />
          <InfoRow label="Wholesale Cost" value={formatCurrency(vehicle.key_blank_cost)} />
          <InfoRow label="Dimensions" value={vehicle.key_blank_dimensions} />
          <InfoRow label="Cut Specification" value={vehicle.key_blank_cut_specification} />
          <InfoRow label="Cutting Machine" value={vehicle.key_blank_cutting_machine} />
          <InfoRow label="Cutting Method" value={vehicle.cutting_method} />
          <InfoRow label="Frequency" value={vehicle.key_blank_frequency || vehicle.frequency} />
          {vehicle.key_blank_alternatives?.length > 0 && (
            <div className="pt-2">
              <p className="text-sm text-muted-foreground mb-1">Alternative Part Numbers:</p>
              <ListBlock items={vehicle.key_blank_alternatives} />
            </div>
          )}
          {vehicle.key_blank_notes && (
            <div className="pt-2 p-3 bg-cyan-500/5 border border-cyan-500/20 rounded">
              <p className="text-sm font-semibold mb-1 text-cyan-500">📝 Notes:</p>
              <p className="text-sm">{vehicle.key_blank_notes}</p>
            </div>
          )}
          <RetailerPricingTable 
            pricing={vehicle.key_blank_retailer_pricing} 
            title="Key Blank — Retailer Price Comparison" 
          />
        </div>
      );

    case 'programming':
      return (
        <div className="space-y-3" data-testid="section-programming">
          <InfoRow label="Method" value={vehicle.programming_method} />
          <InfoRow label="Recommended Programmer" value={vehicle.recommended_programmer} />
          <InfoRow label="PIN Required" value={vehicle.pin_code_required} />
          <InfoRow label="PIN Retrieval" value={vehicle.pin_code_retrieval_method} />
          <InfoRow label="Minimum Keys Required" value={vehicle.minimum_keys_required} />
          <InfoRow label="Estimated Time" value={vehicle.estimated_time_minutes ? `${vehicle.estimated_time_minutes} min` : null} />
          
          {vehicle.compatible_programmers?.length > 0 && (
            <div className="pt-2">
              <p className="text-sm font-semibold mb-2">Compatible Programmers:</p>
              <div className="flex flex-wrap gap-2">
                {vehicle.compatible_programmers.map((p, idx) => (
                  <Badge key={idx} variant="secondary">{p}</Badge>
                ))}
              </div>
            </div>
          )}

          {vehicle.add_key_procedure && (
            <div className="pt-3">
              <p className="text-sm font-semibold mb-1 text-green-500">✓ Add Key Procedure:</p>
              <ProcedureBlock text={vehicle.add_key_procedure} />
            </div>
          )}

          {vehicle.all_keys_lost_procedure && (
            <div className="pt-3">
              <p className="text-sm font-semibold mb-1 text-orange-500">⚠ All Keys Lost Procedure:</p>
              <ProcedureBlock text={vehicle.all_keys_lost_procedure} />
            </div>
          )}

          {vehicle.obd_procedure && (
            <div className="pt-3">
              <p className="text-sm font-semibold mb-1">OBD Procedure:</p>
              <ProcedureBlock text={vehicle.obd_procedure} />
            </div>
          )}
        </div>
      );

    case 'equipment':
      return (
        <div className="space-y-3">
          {vehicle.compatible_programmers?.length > 0 && (
            <div>
              <p className="text-sm font-semibold mb-2">Compatible Programmers:</p>
              <ListBlock items={vehicle.compatible_programmers} />
            </div>
          )}
          {vehicle.cable_adaptor_requirements?.length > 0 && (
            <div>
              <p className="text-sm font-semibold mb-2">Cables & Adaptors:</p>
              <ListBlock items={vehicle.cable_adaptor_requirements} />
            </div>
          )}
          {vehicle.special_requirements?.length > 0 && (
            <div>
              <p className="text-sm font-semibold mb-2">Special Requirements:</p>
              <ListBlock items={vehicle.special_requirements} />
            </div>
          )}
          {vehicle.stock_essentials?.length > 0 && (
            <div>
              <p className="text-sm font-semibold mb-2">Stock Essentials:</p>
              <ListBlock items={vehicle.stock_essentials} />
            </div>
          )}
          <RetailerPricingTable 
            pricing={vehicle.programmer_retailer_pricing} 
            title="Recommended Programmer — Retailer Price Comparison" 
          />
        </div>
      );

    case 'ignition':
      return (
        <div className="space-y-2">
          <InfoRow label="Ignition Info" value={vehicle.ignition_info ? null : 'See notes below'} />
          <InfoRow label="Immobilizer" value={vehicle.immobilizer_type} />
          <InfoRow label="Smart Key Type" value={vehicle.smart_key_type} />
          <InfoRow label="Remote Type" value={vehicle.remote_type} />
          <InfoRow label="Emergency Key Type" value={vehicle.emergency_key_type} />
          {vehicle.ignition_info && (
            <div className="pt-2 p-3 bg-amber-500/5 border border-amber-500/20 rounded">
              <p className="text-sm font-semibold mb-1 text-amber-500">⚡ Ignition Cylinder Info:</p>
              <ProcedureBlock text={vehicle.ignition_info} />
            </div>
          )}
          <RetailerPricingTable 
            pricing={vehicle.ignition_cylinder_retailer_pricing} 
            title="Ignition Cylinder — Retailer Price Comparison" 
          />
        </div>
      );

    case 'diagnostics':
      return (
        <div className="space-y-3">
          {vehicle.add_key_procedure && (
            <div>
              <p className="text-sm font-semibold mb-1">Add Key:</p>
              <ProcedureBlock text={vehicle.add_key_procedure} />
            </div>
          )}
          {vehicle.all_keys_lost_procedure && (
            <div>
              <p className="text-sm font-semibold mb-1">All Keys Lost:</p>
              <ProcedureBlock text={vehicle.all_keys_lost_procedure} />
            </div>
          )}
          {vehicle.eeprom_procedure && (
            <div>
              <p className="text-sm font-semibold mb-1">EEPROM Procedure:</p>
              <ProcedureBlock text={vehicle.eeprom_procedure} />
            </div>
          )}
        </div>
      );

    case 'field-tips':
      return (
        <div className="space-y-3">
          {vehicle.field_tips ? (
            <div className="p-3 bg-yellow-500/5 border border-yellow-500/20 rounded-lg">
              <p className="text-sm">💡 {vehicle.field_tips}</p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">No field tips available</p>
          )}
          <InfoRow label="Coverage Notes" value={vehicle.coverage_notes} />
          <InfoRow label="Universal Key" value={vehicle.universal_key_compatible} />
          {vehicle.universal_key_notes && (
            <div className="pt-2">
              <p className="text-sm font-semibold mb-1">Universal Key Notes:</p>
              <ProcedureBlock text={vehicle.universal_key_notes} />
            </div>
          )}
        </div>
      );

    case 'pricing':
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
              <p className="text-xs text-muted-foreground">Cost</p>
              <p className="text-lg font-bold text-red-500">{formatCurrency(vehicle.locksmith_cost)}</p>
            </div>
            <div className="p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg">
              <p className="text-xs text-muted-foreground">Retail</p>
              <p className="text-lg font-bold text-blue-500">{formatCurrency(vehicle.suggested_retail_price)}</p>
            </div>
            <div className="p-3 bg-green-500/5 border border-green-500/20 rounded-lg">
              <p className="text-xs text-muted-foreground">Profit</p>
              <p className="text-lg font-bold text-green-500">{formatCurrency(vehicle.estimated_profit || (vehicle.suggested_retail_price && vehicle.locksmith_cost ? vehicle.suggested_retail_price - vehicle.locksmith_cost : null))}</p>
            </div>
          </div>
          <InfoRow label="Key Blank Cost" value={formatCurrency(vehicle.key_blank_cost)} />
        </div>
      );

    case 'warnings':
      return (
        <div className="space-y-3">
          {vehicle.warnings?.length > 0 ? (
            <div>
              <p className="text-sm font-semibold mb-2 text-red-500">⚠ Warnings:</p>
              <ListBlock items={vehicle.warnings} />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">No warnings documented</p>
          )}
          {vehicle.common_issues?.length > 0 && (
            <div>
              <p className="text-sm font-semibold mb-2 text-orange-500">Common Issues:</p>
              <ListBlock items={vehicle.common_issues} />
            </div>
          )}
        </div>
      );

    case 'door-locks':
      return (
        <div className="space-y-3">
          {vehicle.door_lock_info ? (
            <div className="p-3 bg-indigo-500/5 border border-indigo-500/20 rounded">
              <p className="text-sm font-semibold mb-1 text-indigo-500">🔒 Door Lock Info:</p>
              <ProcedureBlock text={vehicle.door_lock_info} />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">Standard door lock cylinder. Consult specific vehicle documentation for picking/decoding info.</p>
          )}
          <RetailerPricingTable 
            pricing={vehicle.door_lock_retailer_pricing} 
            title="Door Lock Cylinder — Retailer Price Comparison" 
          />
        </div>
      );

    case 'transponder':
      return (
        <div className="space-y-2">
          <InfoRow label="Chip Type" value={vehicle.chip_type} />
          <InfoRow label="Immobilizer Type" value={vehicle.immobilizer_type} />
          <InfoRow label="Frequency" value={vehicle.frequency} />
          <InfoRow label="Programming Required" value={vehicle.key_blank_programming_required} />
          <RetailerPricingTable 
            pricing={vehicle.transponder_retailer_pricing} 
            title="Transponder Chip — Retailer Price Comparison" 
          />
        </div>
      );

    case 'smart-key':
      return (
        <div className="space-y-2">
          <InfoRow label="Smart Key Type" value={vehicle.smart_key_type} />
          <InfoRow label="Remote Type" value={vehicle.remote_type} />
          <InfoRow label="Frequency" value={vehicle.frequency} />
          <InfoRow label="Emergency Key" value={vehicle.emergency_key_type} />
        </div>
      );

    case 'service-bulletins':
      return (
        <div className="space-y-3">
          {vehicle.service_bulletins?.length > 0 ? (
            <ListBlock items={vehicle.service_bulletins} />
          ) : (
            <p className="text-sm text-muted-foreground italic">No service bulletins documented for this vehicle</p>
          )}
        </div>
      );

    case 'troubleshooting':
      return (
        <div className="space-y-3">
          {vehicle.troubleshooting_tips?.length > 0 ? (
            <ListBlock items={vehicle.troubleshooting_tips} />
          ) : vehicle.common_issues?.length > 0 ? (
            <ListBlock items={vehicle.common_issues} />
          ) : (
            <p className="text-sm text-muted-foreground italic">No troubleshooting info available</p>
          )}
        </div>
      );

    case 'cost-breakdown':
      return (
        <div className="space-y-2">
          <InfoRow label="Key Blank Cost" value={formatCurrency(vehicle.key_blank_cost)} />
          <InfoRow label="Locksmith Cost" value={formatCurrency(vehicle.locksmith_cost)} />
          <InfoRow label="Suggested Retail" value={formatCurrency(vehicle.suggested_retail_price)} />
          <InfoRow label="Estimated Profit" value={formatCurrency(vehicle.estimated_profit)} />
          <InfoRow label="Time Estimate" value={vehicle.estimated_time_minutes ? `${vehicle.estimated_time_minutes} minutes` : null} />
        </div>
      );

    case 'ecu-bcm':
      return (
        <div className="space-y-2">
          <InfoRow label="ECU Reflash Required" value={vehicle.ecu_reflash_required} />
          <InfoRow label="Requires ECU Removal" value={vehicle.requires_ecu_removal} />
          <InfoRow label="ECU Location" value={vehicle.ecu_location} />
          <InfoRow label="BCM Location" value={vehicle.bcm_location} />
          {vehicle.ecu_reflash_notes && (
            <div className="pt-2">
              <p className="text-sm font-semibold mb-1">ECU Reflash Notes:</p>
              <ProcedureBlock text={vehicle.ecu_reflash_notes} />
            </div>
          )}
          {vehicle.ecu_removal_notes && (
            <div className="pt-2">
              <p className="text-sm font-semibold mb-1">Removal Notes:</p>
              <ProcedureBlock text={vehicle.ecu_removal_notes} />
            </div>
          )}
        </div>
      );

    case 'supplier':
      return (
        <div className="space-y-2">
          <InfoRow label="Primary Supplier" value={vehicle.supplier_name || vehicle.key_blank_source} />
          <InfoRow label="Key Source" value={vehicle.key_blank_source} />
          <InfoRow label="Cutting Machine" value={vehicle.key_blank_cutting_machine} />
          {vehicle.coverage_notes && (
            <div className="pt-2">
              <p className="text-sm font-semibold mb-1">Coverage Notes:</p>
              <ProcedureBlock text={vehicle.coverage_notes} />
            </div>
          )}
        </div>
      );

    default:
      return <p className="text-sm text-muted-foreground italic">No data</p>;
  }
};

export default function VehicleInfoAccordion({ vehicle }) {
  const [expanded, setExpanded] = useState(() => {
    const initial = {};
    SECTIONS.forEach((s) => {
      if (s.alwaysOpen) initial[s.id] = true;
    });
    return initial;
  });

  const toggle = (id) => {
    const section = SECTIONS.find((s) => s.id === id);
    if (section?.alwaysOpen) return;
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (!vehicle) return null;

  return (
    <div className="space-y-3 fade-in" data-testid="vehicle-info-accordion">
      {SECTIONS.map(({ id, title, icon: Icon, color, alwaysOpen }) => {
        const isOpen = expanded[id];
        return (
          <div
            key={id}
            className={`border-2 rounded-2xl overflow-hidden transition-all duration-300 shadow-md ${COLOR_MAP[color]}`}
            data-testid={`accordion-${id}`}
          >
            {alwaysOpen ? (
              <div className="w-full flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <Icon className={`w-5 h-5 ${ICON_COLOR_MAP[color]}`} />
                  <h3 className="font-bold text-base">{title}</h3>
                </div>
                <Badge variant="outline" className="text-[10px] uppercase tracking-wider">Always On</Badge>
              </div>
            ) : (
              <button
                onClick={() => toggle(id)}
                className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                data-testid={`toggle-${id}`}
              >
                <div className="flex items-center gap-3">
                  <Icon className={`w-5 h-5 ${ICON_COLOR_MAP[color]}`} />
                  <h3 className="font-bold text-base">{title}</h3>
                </div>
                <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
            )}

            {isOpen && (
              <div className="px-4 pb-4 border-t border-current/10 pt-3">
                {renderSection(id, vehicle)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
